import { api } from '@/api';
import {
  DEFAULT_DOCUMENTS_PAGE_SIZE,
  MAX_DOCUMENTS_PAGE_SIZE,
  type CompanyAccess,
  type CreateDocumentFileInput,
  type CreateDocumentFileObjectInput,
  type DocumentBreadcrumbItem,
  type DocumentFile,
  type DocumentFileDto,
  type DocumentFileObject,
  type DocumentFileObjectDto,
  type DocumentFolder,
  type DocumentFolderDto,
  type FileDetail,
  type FileDetailDto,
  type FolderContentsParams,
  type FolderContentsResponse,
  type FolderContentsResponseDto,
  type UpdateDocumentFileInput,
  type UpdateDocumentFileObjectInput,
  type UploadUrlResponseDto
} from '../types';

export function normalizeLimit(limit?: number) {
  return Math.min(
    Math.max(limit ?? DEFAULT_DOCUMENTS_PAGE_SIZE, 1),
    MAX_DOCUMENTS_PAGE_SIZE
  );
}


export function normalizeDocumentFileObject(
  fileObject: DocumentFileObjectDto
): DocumentFileObject {
  return {
    id: fileObject.id,
    fileId: fileObject.fileId ?? '',
    languageId: fileObject.languageId ?? null,
    fileSize: fileObject.fileSize ?? null,
    isAvailable: fileObject.isAvailable ?? false,
    updatedAt: fileObject.updatedAt ?? null,
    deletedAt: fileObject.deletedAt ?? null,
    metadata: fileObject.metadata ?? {},
    name: fileObject.name ?? "",
    originalFileName: fileObject.originalFileName ?? null,
  };
}

function normalizeFile(file: DocumentFileDto): DocumentFile {
  return {
    type: 'file',
    id: file.id,
    name: file.name,
    fileType: file.fileType,
    category: file.category,
    folderId: file.folderId,
    userId: file.userId,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt,
    deviceAccess: file.deviceAccess ?? [],
    ...(file.fileObject
      ? {
        fileObject: normalizeDocumentFileObject(file.fileObject),
      }
      : {}),
    ...(file.fileObjects
      ? {
        fileObjects: file.fileObjects.map(normalizeDocumentFileObject),
      }
      : {}),
  };
}

function omitUndefined<T extends Record<string, unknown>>(input: T) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined)
  ) as T;
}

function isWrappedDocumentFileResponse(
  response: DocumentFileDto | { file: DocumentFileDto }
): response is { file: DocumentFileDto } {
  return (
    typeof response === 'object' &&
    response !== null &&
    'file' in response &&
    typeof response.file === 'object' &&
    response.file !== null
  );
}

function normalizeFolderContentsResponse(
  response: FolderContentsResponseDto
): FolderContentsResponse {
  return {
    folders: response.folders.map((f) => ({ ...f, type: 'folder' as const })),
    folderNextCursor: response.folderNextCursor ?? null,
    files: response.files.map(normalizeFile),
    fileNextCursor: response.fileNextCursor ?? null,
    currentFolder: response.currentFolder ?? null,
  };
}

export async function fetchFolderContents(
  companyId: string,
  folderId?: string,
  params?: FolderContentsParams
): Promise<FolderContentsResponse> {
  const endpoint = folderId
    ? `/v1/companies/${companyId}/documents/folders/${folderId}`
    : `/v1/companies/${companyId}/documents`;
  const response = await api.get<FolderContentsResponseDto>(endpoint, {
    params: {
      limit: normalizeLimit(params?.limit),
      search: params?.search,
      folderCursor: params?.folderCursor,
      fileCursor: params?.fileCursor,
    },
  });

  return normalizeFolderContentsResponse(response);
}

export async function fetchFolderAncestors(
  folderId: string
): Promise<DocumentBreadcrumbItem[]> {
  const response = await api.get<DocumentBreadcrumbItem[]>(
    `/v1/documents/folders/${folderId}/ancestors`
  );
  return Array.isArray(response) ? response : [];
}

export async function createFolder(input: {
  name: string;
  parentId: string | null;
}): Promise<DocumentFolder> {
  const response = await api.post<DocumentFolderDto>(
    '/v1/documents/folders',
    input
  );
  return { ...response, type: 'folder' as const };
}

export async function renameFolder(
  folderId: string,
  input: { name: string }
): Promise<void> {
  await api.patch<void>(`/v1/documents/folders/${folderId}`, input);
}

export async function deleteFolder(folderId: string): Promise<void> {
  await api.delete<void>(`/v1/documents/folders/${folderId}`);
}

export async function moveFolder(
  folderId: string,
  input: { newParentId: string | null }
): Promise<void> {
  await api.post<void>(`/v1/documents/folders/${folderId}/move`, input);
}

export async function createDocumentFile(
  input: CreateDocumentFileInput
): Promise<DocumentFile> {
  const response = await api.post<DocumentFileDto | { file: DocumentFileDto }>(
    '/v1/documents/files',
    omitUndefined({
      folderId: input.folderId,
      name: input.name,
      category: input.category,
      fileType: input.fileType,
      companyLevel: input.companyLevel,
      deviceModelId: input.deviceModelId,
      userLevel: input.userLevel,
    })
  );

  const filePayload = isWrappedDocumentFileResponse(response)
    ? response.file
    : response;

  if (!filePayload?.id) {
    throw new Error('Create file response did not include a valid file payload');
  }

  return normalizeFile(filePayload);
}

export async function createDocumentFileObject(
  fileId: string,
  input: CreateDocumentFileObjectInput
): Promise<DocumentFileObject> {
  const response = await api.post<DocumentFileObjectDto>(
    `/v1/documents/files/${fileId}/objects`,
    omitUndefined({
      languageId: input.languageId,
      fileSize: input.fileSize,
      name: input.name,
      originalFileName: input.originalFileName,
    })
  );

  return normalizeDocumentFileObject(response);
}

export async function getDocumentFileUploadUrl(
  fileId: string,
  objectId: string,
  filename: string
): Promise<UploadUrlResponseDto> {
  const response = await api.get<UploadUrlResponseDto>(
    `/v1/documents/files/${fileId}/objects/${objectId}/upload-url`,
    {
      params: { filename },
    }
  );

  return response;
}

export async function setStoredFileActive(
  fileId: string,
  objectId: string,
  storedFileId: string
): Promise<void> {
  await api.post<void>(
    `/v1/documents/files/${fileId}/objects/${objectId}/stored-files/${storedFileId}/activate`
  );
}

export async function uploadDocumentFileBinary(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();

    // Handle abort signal for cleanup
    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
        reject(new Error('Upload aborted'));
      });
    }

    request.open('PUT', uploadUrl);
    request.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream'
    );

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }

      reject(
        new Error(`Upload failed: ${request.status} ${request.statusText}`)
      );
    };

    request.onerror = () => reject(new Error('Upload failed'));
    request.onabort = () => reject(new Error('Upload aborted'));
    request.send(file);
  });
}

function normalizeCompanyAccess(
  raw: FileDetailDto['companyAccess']
): CompanyAccess | null {
  if (!raw) return null;
  return {
    documentId: raw.documentId,
    companyLevel: raw.companyLevel,
    createdBy: raw.createdBy,
    createdAt: raw.createdAt,
  };
}

function normalizeFileDetail(dto: FileDetailDto): FileDetail {
  return {
    file: normalizeFile(dto.file),
    fileObjects: dto.fileObjects.map(normalizeDocumentFileObject),
    companyAccess: normalizeCompanyAccess(dto.companyAccess),
    deviceAccess: dto.deviceAccess ?? [],
    folderId: dto.folderId ?? null,
    shareLink: dto.shareLink,
  };
}

export async function fetchDocumentFile(
  fileId: string,
  params?: { includeUnavailable?: boolean }
): Promise<FileDetail> {
  const response = await api.get<FileDetailDto>(
    `/v1/documents/files/${fileId}`,
    { params: omitUndefined({ includeUnavailable: params?.includeUnavailable || undefined }) }
  );
  return normalizeFileDetail(response);
}

export async function updateDocumentFile(
  fileId: string,
  input: UpdateDocumentFileInput
): Promise<void> {
  await api.patch<void>(
    `/v1/documents/files/${fileId}`,
    omitUndefined({
      name: input.name,
      folderId: input.folderId,
      category: input.category,
      companyLevel: input.companyLevel,
      accessCategory: input.accessCategory,
    })
  );
}

export async function deleteDocumentFile(fileId: string): Promise<void> {
  await api.delete<void>(`/v1/documents/files/${fileId}`);
}

export async function moveDocumentFile(
  fileId: string,
  input: { newFolderId: string | null }
): Promise<void> {
  await api.post<void>(`/v1/documents/files/${fileId}/move`, {
    newFolderId: input.newFolderId,
  });
}

export async function updateDocumentFileObject(
  fileId: string,
  objectId: string,
  input: UpdateDocumentFileObjectInput
): Promise<void> {
  await api.put<void>(
    `/v1/documents/files/${fileId}/objects/${objectId}`,
    omitUndefined({
      languageId: input.languageId,
      fileSize: input.fileSize,
      name: input.name,
      originalFileName: input.originalFileName,
    })
  );
}

export async function deleteDocumentFileObject(
  fileId: string,
  objectId: string
): Promise<void> {
  await api.delete<void>(`/v1/documents/files/${fileId}/objects/${objectId}`);
}

export async function getDocumentFileDownloadUrl(
  fileId: string,
  objectId: string,
  download: boolean
): Promise<string> {
  const response = await api.get<UploadUrlResponseDto>(
    `/v1/documents/files/${fileId}/objects/${objectId}/download-url`,
    { params: { download: download } }
  );
  return response.url;
}

export async function addDocumentFileDeviceAccess(
  fileId: string,
  input: { deviceModelId: number; userLevel: number }
): Promise<void> {
  await api.post<void>(`/v1/documents/files/${fileId}/access/device`, {
    deviceModelId: input.deviceModelId,
    userLevel: input.userLevel,
  });
}

export async function removeDocumentFileDeviceAccess(
  fileId: string,
  input: { deviceModelId: number }
): Promise<void> {
  await api.delete<void>(`/v1/documents/files/${fileId}/access/device`, {
    data: { deviceModelId: input.deviceModelId },
  });
}

/** Enables public sharing for a file and returns the generated share link. */
export async function enableDocumentFileShare(
  fileId: string
): Promise<{ shareLink: string }> {
  return api.post<{ shareLink: string }>(`/v1/documents/files/${fileId}/share`);
}

/** Disables public sharing for a file, revoking the existing share link. */
export async function disableDocumentFileShare(fileId: string): Promise<void> {
  await api.delete<void>(`/v1/documents/files/${fileId}/share`);
}
